<template>
  <main class="main main--has-floating-buttons grid--dense grid--min-height">
    <spinner-overlay v-if="saveInProgress" />
    <portal to="headerTitle">
      {{ $t(`email.title.${$route.meta.parent}`, [id2number(parentId)]) }}
    </portal>
    <card :title="$t('pageDocument.card.recipient')" cols="12 3@desktop">
      <compact-guest-profile-editor v-model="recipientGuestProfileId" :search="true" :label="$t('pageDocument.recipientGuestProfileId')" type="Guest" />
    </card>
    <card :title="$t('pageDocument.card.document')" cols="12 3@desktop">
      <hm-card-inner>
        <hm-input-select id="template"
                         v-model="templateId"
                         :label="$t('pageDocument.template')"
                         :parent-errors="errors"
                         name="templateId"
                         cols="12"
                         @blur="$v.templateId.$touch()">
          <option v-for="template in templates" :value="template.id">{{ template.name }}</option>
        </hm-input-select>
        <hm-input v-model="subject"
                  :label="$t('pageDocument.subject')"
                  :parent-errors="errors"
                  name="subject"
                  cols="12"
                  @blur="$v.subject.$touch()" />
      </hm-card-inner>
    </card>
    <tabable-card id="content-data" cols="12 6@desktop" rows="4">
      <tabable-card-content :center="isEmpty(content)" :title="$t('pageDocument.card.preview')">
        <hm-card-inner :full-size="true">
          <hm-col v-if="!isEmpty(content)" cols="12">
            <ckeditor v-model="content" :incard="true" height="500" />
          </hm-col>
          <hint v-else :text="$t('pageDocument.hintSelectTemplate')" />
        </hm-card-inner>
      </tabable-card-content>
      <tabable-card-content v-if="hasRole('ROLE_SYSADMIN')" :title="$t('pageDocument.card.rawData')">
        <hm-card-inner :full-size="true">
          <pre v-html="jsonData"></pre>
        </hm-card-inner>
      </tabable-card-content>
    </tabable-card>

    <hm-floating-buttons ref="floatingButtons">
      <hm-floating-button :disabled="$v.$anyError || isEmpty(templateId) || isEmpty(subject)"
                          :label="$t('pageDocument.actions.createPDF')"
                          icon="save"
                          color="mint"
                          @click="createPDF" />
    </hm-floating-buttons>
  </main>
</template>


<script>
/* eslint func-names: 0 */

import { isEmpty, isNil, last, isFunction, sortBy, head } from 'lodash';
import uuid from 'uuid';
import HotelmeisterMixin from '../../mixins/HotelmeisterMixin';
import HmCardInner from '../lib/layout/HmCardInner';
import HmSelect from '../lib/inputs/HmSelect';
import TabableCard from '../lib/layout/TabableCard';
import TabableCardContent from '../lib/layout/TabableCardContent';
import ValidateMixin from '../../mixins/ValidateMixin';

export default {
  name: 'PageDocument',
  components: { TabableCardContent, TabableCard, HmSelect, HmCardInner },
  mixins: [HotelmeisterMixin, ValidateMixin],

  data() {
    return {
      recipientGuestProfileId: null,
      saveInProgress: false,
      templateId: null,
      subject: null,
      content: null,
      language: null,
      renderParams: {
        confirmationCode: this.$route.params.confirmationNumberOrReservationGroupId,
        guestOfferId: this.$route.params.guestOfferId,
        inquiryId: this.$route.params.inquiryId,
        reservationIds: isNil(this.$route.params.reservationId) ? null : [this.$route.params.reservationId],
        guestProfileId: this.$route.params.guestProfileId,
        invoiceNumber: null,
        receiptIdentifier: {
          parentId: null,
          receiptId: null,
        },
        folioIdentifier: {
          parentId: null,
          folioId: null,
        },
        language: null,
      },
      jsonData: null,
      templates: [],
    };
  },

  validations() {
    return {
      subject: this.$hv__createValidators('required', this.$t('pageDocument.subject')),
      templateId: this.$hv__createValidators('required', this.$t('pageDocument.template')),
    };
  },

  computed: {
    parentId: function () {
      return this.$route.params.reservationId
        || this.$route.params.guestProfileId
        || this.$route.params.guestOfferId
        || this.$route.params.inquiryId
        || this.$route.params.confirmationNumberOrReservationGroupId;
    },
    secondaryId: function () {
      return this.$route.params.folioId;
    },
  },

  watch: {
    templateId: function () {
      this.updateDocument();
    },
  },

  mounted: function () {
  },

  created: function () {
    this.updateTemplates();
    this.updateDocument();

    if (!isEmpty(this.$route.params.confirmationNumberOrReservationGroupId)) {
      this.axios.get(this.serviceUrls.reservationGroup.get(this, this.$route.params.confirmationNumberOrReservationGroupId))
        .then((response) => {
          const reservationGroup = response.data.data;

          this.renderParams.reservationIds = reservationGroup.roomReservations.map(it => it.reservationId);
          this.recipientGuestProfileId = isNil(reservationGroup.booker) ? null : reservationGroup.booker.id;
        });
    }

    if (!isEmpty(this.$route.params.reservationId)) {
      this.axios
        .get(this.serviceUrls.reservation.get(this, this.$route.params.reservationId))
        .then((response) => {
          const reservation = response.data.data;
          this.recipientGuestProfileId = reservation.stayDetails.primaryGuestProfileId;
          if (isNil(this.recipientGuestProfileId)) this.recipientGuestProfileId = reservation.stayDetails.companyProfileId;
          if (isNil(this.recipientGuestProfileId)) this.recipientGuestProfileId = reservation.stayDetails.travelAgentProfileId;
        });
    }
  },

  methods: {
    createPDF() {
      this.saveInProgress = true;
      this.axios.post(this.serviceUrls[this.$route.meta.parentServiceUrl].document(this, this.parentId, this.secondaryId), {
        id: uuid.v4(),
        subject: this.subject,
        content: this.content,
        recipientId: this.recipientGuestProfileId,
        language: this.language,
        templateId: this.templateId,
      })
        .then((response) => {
          this.showSuccessMessage();
          if (!isEmpty(this.$route.meta.parent)) {
            this.$router.push({
              name: this.$route.meta.parent,
              params: this.$route.params,
            });
          }
        })
        .catch((error) => {
          this.sendInProgress = false;
          this.showMessages(error.response);
        })
        .finally(() => {
          this.sendInProgress = false;
        });
    },
    id2number: function (value) {
      if (isEmpty(value)) return '';
      return last(value.split('::'));
    },
    updateTemplates() {
      this.axios.get(this.serviceUrls.templateHelper.filter(this, 'Document'))
        .then((response) => {
          let filteredTemplates = response.data.data;

          filteredTemplates = filteredTemplates.filter(it => it.type === 'GenericDocument');

          filteredTemplates = sortBy(filteredTemplates, ['name']);

          this.templates = filteredTemplates;

          if (filteredTemplates.length === 1) {
            this.templateId = filteredTemplates[0].id;
          }
        });
    },
    updateDocument() {
      if (isEmpty(this.templateId)) {
        this.content = null;
        this.subject = null;
        this.jsonData = null;
      } else {
        this.axios.post(this.serviceUrls.templateHelper.render(this, this.templateId), this.renderParams)
          .then((response) => {
            this.content = response.data.data.content;
            this.subject = response.data.data.title;
            this.language = response.data.data.language;
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
        this.axios.post(this.serviceUrls.templateHelper.data(this), this.renderParams)
          .then((response) => {
            this.jsonData = response.data.data;
          })
          .catch((error) => {
            this.showMessages(error.response);
          });
      }
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';
</style>



// WEBPACK FOOTER //
// src/components/pages/PageDocument.vue